import api from "@/config/api";
import { Forms } from "@/types/form";
import { Params } from "@/types/utils";
import { getRoutes, handleRouting } from "@/utils/helpers";
import { log } from "@/utils/logger";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

const useSubmit = () => {
    const routes = getRoutes();
    const { oid } = useParams<Params>();

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (settings: Forms["Settings"]) => {
            return await api.put(`/organizations/${oid}`, { data: settings });
        },
        onMutate: async newSettings => {
            await queryClient.cancelQueries({ queryKey: ["organization", oid] });

            const previousSettings = queryClient.getQueryData<Forms["Settings"]>(["organization"]);

            queryClient.setQueryData<Forms["Settings"]>(["organization"], settings => {
                return { ...settings, ...newSettings };
            });

            return { previousSettings };
        },
        onError: (error, _, context: { previousSettings?: Forms["Settings"] } | undefined) => {
            queryClient.setQueryData(["organization"], context?.previousSettings);

            log.error(error, "onSubmit");
        },
        onSuccess: () => {
            log.success("Company has been successfully updated.");

            queryClient.invalidateQueries({ queryKey: ["organization", oid] });

            handleRouting(routes.organization);
        },
    });
};

export { useSubmit };
